"use client";

import { motion } from "motion/react";
import { TrendingUp, TrendingDown, Calendar, Info, ChevronLeft, ChevronRight } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import type { ParsedPriceHistoryEntry } from "@/lib/data/property-review-data";

// Helper function to format price in millions or thousands
const formatNairaPrice = (price: number): string => {
  if (price >= 1000000) {
    return `${(price / 1000000).toFixed(1)}M`;
  } else if (price >= 1000) {
    return `${(price / 1000).toFixed(1)}K`;
  } else {
    return `${price}`;
  }
};

interface PriceHistoryProps {
  priceHistory?: ParsedPriceHistoryEntry[];
}

const ITEMS_PER_PAGE = 5;

const PriceHistory = ({ priceHistory = [] }: PriceHistoryProps) => {
  const [currentPage, setCurrentPage] = useState(1);
  
  const totalPages = Math.ceil(priceHistory.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentItems = priceHistory.slice(startIndex, endIndex);

  const handlePrevPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Price History</h2>
          <p className="text-xs text-gray-500 leading-tight">Track rent changes over time</p>
        </div>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger>
              <Info className="h-4 w-4 text-gray-400 hover:text-gray-600" />
            </TooltipTrigger>
            <TooltipContent>
              <p>Historical rental prices for this property</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {/* Price Timeline */}
      <div className="space-y-2 min-h-[220px]">
        {priceHistory.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-[220px] text-gray-500">
            <Calendar className="h-12 w-12 mb-3 text-gray-300" />
            <p className="text-sm font-medium">No price history available</p>
            <p className="text-xs text-center">
              Price history will appear here when rental data is available
            </p>
          </div>
        ) : (
          <>
            {currentItems.map((item, index) => (
              <motion.div
                key={item.date}
                initial={{ opacity: 0, x: -12 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.07 }}
                className="flex items-center justify-between p-2 rounded-md hover:bg-gray-50 transition-colors duration-200"
              >
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-rentwise-blue-100 flex items-center justify-center">
                    <Calendar className="h-4 w-4 text-rentwise-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 text-sm leading-tight">{item.date}</p>
                    <p className="text-xs text-gray-400 leading-tight">{item.note}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="text-right">
                    <div className="flex items-center gap-0.5">
                      <span className="text-gray-600 font-medium text-sm">₦</span>
                      <span className="font-semibold text-gray-900 text-sm">{formatNairaPrice(item.price)}</span>
                    </div>
                    {item.change !== 0 && (
                      <div className={`flex items-center text-xs ${
                        item.change > 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {item.change > 0 ? (
                          <TrendingUp className="h-3 w-3 mr-0.5" />
                        ) : (
                          <TrendingDown className="h-3 w-3 mr-0.5" />
                        )}
                        <span>{Math.abs(item.change)}%</span>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </>
        )}
      </div>

      {/* Pagination Controls - Only show if there's data */}
      {priceHistory.length > 0 && (
        <div className="mt-3 flex items-center justify-between border-t pt-2">
          <div className="text-xs text-gray-400">
            Showing {startIndex + 1}-{Math.min(endIndex, priceHistory.length)} of {priceHistory.length} updates
          </div>
          <div className="flex items-center gap-1.5">
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              className="h-7 px-2 text-xs"
            >
              <ChevronLeft className="h-3 w-3 mr-0.5" />
              Prev
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className="h-7 px-2 text-xs"
            >
              Next
              <ChevronRight className="h-3 w-3 ml-0.5" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PriceHistory; 